import React, { createContext, useContext, ReactNode, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { ClientSelectorTableItem } from '@frontend/features/clients/components/ClientSelectorModal';
import { UserSelectorTableItem } from '@frontend/features/users/components/UserSelectorModal';
import { OrganizationsSelectorTableItem } from '@frontend/features/organizations/components/OrganizationSelectorModal';
import { AgenciesSelectorTableItem } from '@frontend/features/agencies/components/AgenciesSelectorModal';
import { TeamsSelectorTableItem } from '@frontend/features/teams/components/TeamsSelectorModal';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';

import { CaseResponseDto } from '@bandido/shared/dtos';

import { useGetCaseById } from '../queries';

interface CaseContextProps {
  caseData?: CaseResponseDto;
  // Modal states
  isOrganizationEditOpen: boolean;
  isAgentEditOpen: boolean;
  isAgencyEditOpen: boolean;
  isTeamEditOpen: boolean;
  isLinkCaseOpen: boolean;
  isCaseNameEditOpen: boolean;
  isStepInstructionsModalOpen: boolean;
  isClientSelectorModalOpen: boolean;

  // Handlers
  onCopyCaseNumber: () => Promise<void>;
  onChangeStatus: (value: string | number) => Promise<void>;
  onChangeIAEnabled: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onChangeStep: (value: string | number) => Promise<void>;
  onChangeWorkflow: (value: string | number) => Promise<void>;
  onChangeAgency: (option: AgenciesSelectorTableItem | null) => Promise<void>;
  onChangeAgent: (option: UserSelectorTableItem | null) => Promise<boolean>;
  onChangeName: (value: string) => Promise<void>;
  onAssignToMe: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => Promise<void>;
  onChangeOrganization: (option: OrganizationsSelectorTableItem | null) => Promise<void>;
  onChangeClient: (clientItem: ClientSelectorTableItem | null) => Promise<void>;
  onChangeTeam: (value: TeamsSelectorTableItem) => Promise<void>;
  onSaveLegacySaleOrder: (value: string) => Promise<void>;
  onSaveLegacyReference: (value: string) => Promise<void>;
  onRemoveRelation: (caseId: number, relationId: number) => Promise<void>;

  // Toggle functions
  toggleClientSelector: () => void;
  toggleAgentSelector: () => void;
  toggleTeamSelector: () => void;
  toggleEditCaseName: () => void;
  toggleIsLinkCaseOpen: () => void;
  toggleOrganizationEdit: () => void;
  toggleAgencyEdit: () => void;
  handleStepInstructionsVisibility: (next: boolean) => () => void;

  // CaseSidebar handlers
  onEditCaseName: () => void;
  onStepInstructionsOpen: () => void;
  onClientSelectorOpen: () => void;
  onOrganizationEditOpen: () => void;
  onAgencyEditOpen: () => void;
  onTeamSelectorOpen: () => void;
  onAgentSelectorOpen: () => void;
  onLinkCaseOpen: () => void;
}

const CaseContext = createContext<CaseContextProps | undefined>(undefined);

interface CaseProviderProps {
  children: ReactNode;
}

export const CaseProvider: React.FC<CaseProviderProps> = ({ children }) => {
  const { myProfile } = useAuth();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id: caseId } = useParams();
  const [isOrganizationEditOpen, setIsOrganizationEditOpen] = useState(false);
  const [isAgentEditOpen, setIsAgentEditOpen] = useState(false);
  const [isAgencyEditOpen, setIsAgencyEditOpen] = useState(false);
  const [isTeamEditOpen, setIsTeamEditOpen] = useState(false);
  const [isLinkCaseOpen, setIsLinkCaseOpen] = useState(false);
  const [isCaseNameEditOpen, setIsCaseNameEditOpen] = useState(false);
  const [isStepInstructionsModalOpen, setIsStepInstructionsModalOpen] = useState(false);
  const [isClientSelectorModalOpen, setIsClientSelectorModalOpen] = useState<boolean>(false);

  const { data } = useGetCaseById({
    id: Number(caseId),
  });

  const handleStepInstructionsVisibility = (next: boolean) => () => {
    setIsStepInstructionsModalOpen(next);
  };

  const handleCopyCaseNumber = async () => {
    if (data?.id) {
      try {
        await navigator.clipboard.writeText(data.id.toString());
        addToast({
          message: t('cases.view.copySuccess'),
          color: 'success',
        });
      } catch (_e) {
        // Fallback para navegadores más antiguos
        const textArea = document.createElement('textarea');
        textArea.value = data.id.toString();
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        addToast({
          message: t('cases.view.copySuccess'),
          color: 'success',
        });
      }
    }
  };

  const handleChangeStatus = async (value: string | number) => {
    try {
      await client.put(`/case/${caseId}`, { status: value });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  };

  const handleChangeIAEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      await client.put(`/case/${caseId}/toggle-ia`, { iaEnabled: event.target.checked });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  };

  const handleChangeStep = async (value: string | number) => {
    try {
      await client.put(`/case/${caseId}`, { step: value });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  };

  const handleChangeWorkflow = async (value: string | number) => {
    try {
      await client.put(`/case/${caseId}`, { workflow: value });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  };

  const handleChangeAgency = async (option: AgenciesSelectorTableItem | null) => {
    if (option) {
      try {
        await client.put(`/case/${caseId}`, { agencyId: option.id });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      } finally {
        setIsAgencyEditOpen(false);
      }
    }
  };

  const handleChangeAgent = async (option: UserSelectorTableItem | null) => {
    if (option) {
      try {
        await client.put(`/case/${caseId}`, { agentId: option.clientId });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
        setIsAgentEditOpen(false);
        void queryClient.invalidateQueries([QueryKeys.getCaseById, Number(caseId)]);
        void queryClient.invalidateQueries(QueryKeys.getAllCases);
        return true;
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      }
    }
    return false;
  };

  const handleChangeName = async (value: string) => {
    if (value) {
      try {
        await client.put(`/case/${caseId}`, { name: value });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      }
    }
  };

  const handleAssignToMe = async (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    if (myProfile) {
      try {
        await client.put(`/case/${caseId}`, { agentId: myProfile.clientId });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      }
    }
  };

  const handleChangeOrganization = async (option: OrganizationsSelectorTableItem | null) => {
    if (option) {
      try {
        await client.put(`/case/${caseId}`, { organizationId: option.id });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      } finally {
        setIsOrganizationEditOpen(false);
      }
    }
  };

  const handleChangeClient = async (clientItem: ClientSelectorTableItem | null) => {
    if (clientItem) {
      try {
        await client.put(`/case/${caseId}`, { clientId: clientItem.id });
        addToast({
          message: t('cases.update.success'),
          color: 'success',
        });
      } catch (_e) {
        addToast({
          message: t('cases.update.error'),
          color: 'error',
        });
      } finally {
        toggleClientSelector();
      }
    }
  };

  const handleChangeTeam = async (value: TeamsSelectorTableItem) => {
    try {
      await client.put(`/case/${caseId}`, { team: value.id });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  };

  const handleSavelegacySalerOrder = useCallback(async (value: string) => {
    try {
      await client.put(`/case/${caseId}`, { legacySaleOrderId: value });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  }, []);

  const handleSaveLegacyReference = useCallback(async (value: string) => {
    try {
      await client.put(`/case/${caseId}`, { legacyReference: value });
      addToast({
        message: t('cases.update.success'),
        color: 'success',
      });
    } catch (_e) {
      addToast({
        message: t('cases.update.error'),
        color: 'error',
      });
    }
  }, []);

  const handleRemoveRelation = async (id: number, relationId: number) => {
    try {
      await client.delete(`/case/${id}/related/${relationId}`);
      addToast({
        message: t('cases.view.relationRemovedSuccess'),
        color: 'success',
      });
      queryClient.invalidateQueries([QueryKeys.getRelatedCases, id]);
    } catch (_e) {
      addToast({
        message: 'cases.view.relationRemovedError',
        color: 'error',
      });
    }
  };

  const toggleOrganizationEdit = () => setIsOrganizationEditOpen((prev) => !prev);
  const toggleAgencyEdit = () => setIsAgencyEditOpen((prev) => !prev);
  const toggleClientSelector = () => setIsClientSelectorModalOpen((prevState) => !prevState);
  const toggleAgentSelector = () => setIsAgentEditOpen((prev) => !prev);
  const toggleTeamSelector = () => setIsTeamEditOpen((prev) => !prev);
  const toggleEditCaseName = () => setIsCaseNameEditOpen((prev) => !prev);
  const toggleIsLinkCaseOpen = () => setIsLinkCaseOpen((prev) => !prev);

  return (
    <CaseContext.Provider
      value={{
        caseData: data,
        // Modal states
        isOrganizationEditOpen,
        isAgentEditOpen,
        isAgencyEditOpen,
        isTeamEditOpen,
        isLinkCaseOpen,
        isCaseNameEditOpen,
        isStepInstructionsModalOpen,
        isClientSelectorModalOpen,

        // Handlers
        onCopyCaseNumber: handleCopyCaseNumber,
        onChangeStatus: handleChangeStatus,
        onChangeIAEnabled: handleChangeIAEnabled,
        onChangeStep: handleChangeStep,
        onChangeWorkflow: handleChangeWorkflow,
        onChangeAgency: handleChangeAgency,
        onChangeAgent: handleChangeAgent,
        onChangeName: handleChangeName,
        onAssignToMe: handleAssignToMe,
        onChangeOrganization: handleChangeOrganization,
        onChangeClient: handleChangeClient,
        onChangeTeam: handleChangeTeam,
        onSaveLegacySaleOrder: handleSavelegacySalerOrder,
        onSaveLegacyReference: handleSaveLegacyReference,
        onRemoveRelation: handleRemoveRelation,

        // Toggle functions
        toggleClientSelector,
        toggleAgentSelector,
        toggleTeamSelector,
        toggleEditCaseName,
        toggleIsLinkCaseOpen,
        toggleOrganizationEdit,
        toggleAgencyEdit,
        handleStepInstructionsVisibility,

        onEditCaseName: toggleEditCaseName,
        onStepInstructionsOpen: handleStepInstructionsVisibility(true),
        onClientSelectorOpen: toggleClientSelector,
        onOrganizationEditOpen: toggleOrganizationEdit,
        onAgencyEditOpen: toggleAgencyEdit,
        onTeamSelectorOpen: toggleTeamSelector,
        onAgentSelectorOpen: toggleAgentSelector,
        onLinkCaseOpen: toggleIsLinkCaseOpen,
      }}
    >
      {children}
    </CaseContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useCaseContext = () => {
  const context = useContext(CaseContext);

  if (context === undefined) {
    throw new Error('useCaseContext must be used within a CaseProvider');
  }

  return context;
};

// Hook de conveniencia para verificar si los sockets están conectados
export const useCaseSocketConnection = () => {
  // const { isConnected } = useCaseContext();
  // return isConnected;
};
